import {
  MAX_UPLOAD_SIZE_BYTE,
  UPLOAD_FILE_LIMIT_TOTAL,
  UPLOAD_LIMIT_SIZE_PER_FILE,
  UPLOAD_LIMIT_SIZE_PER_FILE_BYTE,
} from '@/features/invoices/components/List/Table/Uploader/constant';
import {
  RECEIVED_INVOICE_FILE_ERROR,
  TReceivedInvoiceUpload,
  TUploadFile,
} from '@/features/invoices/components/List/Table/Uploader/type';
import { useTranslation } from '@/i18n';
import { UploadFile, UploadFiles } from '@moneyforward/ap-frontend-components';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

type TValid = {
  inValid: boolean;
  message: string;
};

type TResultErrors = Record<string, { message?: string }[]>;

// API result error keys that should be surfaced as a per-file error message
// instead of a form-level field error.
const FILE_LEVEL_ERROR_KEYS = ['file', 'do_ai_suggest'];
// When a file has multiple file-level errors, only the AI Suggest error is surfaced
const PRIORITIZED_FILE_LEVEL_KEY = 'do_ai_suggest';

export const splitResultErrors = (errors: TResultErrors) => {
  const fileErrorMessages: Record<string, string> = {};
  const fieldErrors: { key: string; message: string }[] = [];
  Object.entries(errors).forEach(([key, value]) => {
    const message = value.map((error) => error.message ?? '').join('');
    if (FILE_LEVEL_ERROR_KEYS.includes(key)) {
      fileErrorMessages[key] = message;
    } else {
      fieldErrors.push({ key, message });
    }
  });
  const fileErrorMsg =
    fileErrorMessages[PRIORITIZED_FILE_LEVEL_KEY] ??
    Object.values(fileErrorMessages).join('\n');
  return { fileErrorMsg, fieldErrors };
};

const fileValidation = (file: UploadFile, t: TFunction): TValid => {
  const checkSizeLimit = (): TValid => {
    const { size } = file;
    const inValid = Boolean(size && size >= UPLOAD_LIMIT_SIZE_PER_FILE_BYTE);
    return {
      inValid,
      message: inValid
        ? t('file_size_limit', {
            limit_size: UPLOAD_LIMIT_SIZE_PER_FILE,
          })
        : '',
    };
  };

  const validation = [checkSizeLimit];
  for (const fn of validation) {
    const valid = fn();
    if (valid.inValid) return valid;
  }
  return {
    inValid: false,
    message: '',
  };
};

export const useValidationFiles = () => {
  const { t } = useTranslation();

  return useCallback(
    (file: TUploadFile) => {
      const { inValid, message } = fileValidation(file, t);
      return { inValid, message };
    },
    [t]
  );
};

export const useFilesValidationStatus = (
  files: TReceivedInvoiceUpload['files']
) => {
  const filesWithError = useMemo(
    () =>
      files.filter(
        (file: TUploadFile) =>
          file?.errorMessage &&
          file?.error === RECEIVED_INVOICE_FILE_ERROR.MAX_SIZE
      ),
    [files]
  );
  return { filesWithError };
};

export const useFilesValidation = (
  files: UploadFiles,
  setFiles: (files: UploadFiles) => void
) => {
  const [maxFileCountVisible, setMaxFileCountVisible] = useState(
    files.length > UPLOAD_FILE_LIMIT_TOTAL
  );
  const onMaxFileCountHidden = useCallback(() => {
    setMaxFileCountVisible(false);
  }, []);

  const maxSizeErr = useMemo(
    () =>
      files.map((f) => f.size ?? 0).reduce((prev, cur) => prev + cur, 0) >
      MAX_UPLOAD_SIZE_BYTE,
    [files]
  );
  const [totalMaxSizeErrVisible, setTotalMaxSizeErrVisible] =
    useState(maxSizeErr);

  useEffect(() => {
    if (files.length > UPLOAD_FILE_LIMIT_TOTAL) {
      setMaxFileCountVisible(true);
      setFiles(files.slice(0, UPLOAD_FILE_LIMIT_TOTAL));
    }
    setTotalMaxSizeErrVisible(maxSizeErr);
    if (files.length === 0) {
      setMaxFileCountVisible(false);
      setTotalMaxSizeErrVisible(false);
    }
  }, [files, maxSizeErr, setFiles]);
  return {
    maxFileCountVisible,
    onMaxFileCountHidden,
    totalMaxSizeErrVisible,
  };
};
